/**
 * AdTrafficGate — recognises shopper traffic from Google Ads / Meta Ads and
 * routes them straight to app-download intent.
 *
 * Detection sources (any one wins):
 *   · `?gclid=…`          → Google Ads
 *   · `?fbclid=…`         → Meta / Facebook Ads
 *   · `?utm_source=google` / `utm_source=meta` / `utm_source=facebook` /
 *     `utm_source=instagram` / `utm_source=tiktok`
 *   · `?src=ads`          → generic escape hatch for any partner
 *
 * Behaviour:
 *   1. Persists the source + campaign + landing to localStorage under
 *      `cyannect.ad_attribution` (JSON) so we can attribute downstream
 *      conversions when the shopper opens the PWA / app.
 *   2. Fires a light-weight backend log to `/api/marketing/ad-landing`
 *      (best-effort, ignored on 404 so the frontend still works when the
 *      endpoint is disabled).
 *   3. Renders a persistent top banner nudging download.
 *   4. On mobile (≤ 767px) also shows a full-screen sheet on first render
 *      with two conversion CTAs — App Store / Google Play / QR — that the
 *      shopper can dismiss to keep browsing the landing page.
 */
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Apple, Play, X, Smartphone, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { urlOnHost } from "@/lib/host";

const STORAGE_KEY = "cyannect.ad_attribution";
const SHEET_DISMISSED_KEY = "cyannect.ad_gate.sheet_dismissed_at";
const SHEET_TTL_MIN = 60; // don't re-nag inside the same hour

const APP_URL = urlOnHost("app", "/coupons");
const appTarget = (store) => urlOnHost("app", store ? `/store/${store}` : "/coupons");

function readParams() {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search);
  const gclid = q.get("gclid");
  const fbclid = q.get("fbclid");
  const src = (q.get("utm_source") || q.get("src") || "").toLowerCase();
  const known = ["google", "meta", "facebook", "instagram", "tiktok", "ads"];
  const utmMatch = known.some((s) => src === s || src.startsWith(s));
  if (!gclid && !fbclid && !utmMatch) return null;
  const source = gclid ? "google_ads"
                    : fbclid ? "meta_ads"
                    : src === "google" ? "google_ads"
                    : src === "meta" || src === "facebook" ? "meta_ads"
                    : src === "instagram" ? "meta_ads"
                    : src === "tiktok" ? "tiktok_ads"
                    : "paid_ads";
  return {
    source,
    utm_source: q.get("utm_source"),
    utm_medium: q.get("utm_medium"),
    utm_campaign: q.get("utm_campaign"),
    utm_content: q.get("utm_content"),
    utm_term: q.get("utm_term"),
    gclid, fbclid,
    // Deep-link target — when the ad has `?store=<slug>` we route the app
    // download flow to `app.cyannect.com/store/<slug>` so the shopper lands
    // on the exact brand they clicked instead of a generic feed.
    store: q.get("store"),
    landing: window.location.pathname + window.location.search,
    ts: Date.now(),
  };
}

function sheetDismissedRecently() {
  try {
    const at = localStorage.getItem(SHEET_DISMISSED_KEY);
    if (!at) return false;
    return (Date.now() - Number(at)) / 60000 < SHEET_TTL_MIN;
  } catch { return false; }
}

const isMobile = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(max-width: 767px)").matches;

export default function AdTrafficGate() {
  const [attr, setAttr] = useState(null);
  const [showSheet, setShowSheet] = useState(false);
  const [qr, setQr] = useState(null);

  useEffect(() => {
    const parsed = readParams();
    if (!parsed) return;
    setAttr(parsed);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed)); } catch {}
    // Fire-and-forget backend log — silently no-op if endpoint missing.
    // Gate sheet bypassed — do not block user navigation
    setShowSheet(false);
  }, []);

  useEffect(() => {
    if (!attr) return;
    QRCode.toDataURL(appTarget(attr.store), {
      margin: 1, width: 220,
      color: { dark: "#0e7490", light: "#ffffff" },
    }).then(setQr).catch(() => setQr(null));
  }, [attr]);

  const dismissSheet = () => {
    try { localStorage.setItem(SHEET_DISMISSED_KEY, String(Date.now())); } catch {}
    setShowSheet(false);
  };

  if (!attr) return null;

  const label = attr.source === "google_ads" ? "Google Ads visitor"
              : attr.source === "meta_ads" ? "Meta Ads visitor"
              : attr.source === "tiktok_ads" ? "TikTok Ads visitor"
              : "Paid Ads visitor";

  return (
    <>
      {/* Persistent top banner */}
      <div className="sticky top-0 z-40 bg-gradient-to-r from-cyan-500 via-emerald-500 to-cyan-500 text-white shadow-lg shadow-cyan-500/20"
           data-testid="ad-gate-banner"
           data-source={attr.source}>
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <Sparkles className="w-4 h-4 shrink-0" />
          <div className="flex-1 min-w-0 text-xs md:text-sm font-semibold">
            <span className="hidden sm:inline">You're here from an ad — </span>
            <span>{attr.store
              ? `Get the Cyannect App to see the ${attr.store} offer.`
              : "Get the Cyannect App to unlock the offer."}</span>
            <span className="text-[10px] opacity-75 ml-2 font-normal">{label}</span>
          </div>
          <a href="#download"
             onClick={(e) => {
               e.preventDefault();
               const el = document.querySelector('[data-testid="download"]');
               if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
             }}
             className="px-3 py-1.5 rounded-full bg-white text-slate-900 text-xs font-semibold inline-flex items-center gap-1 hover:bg-cyan-50 transition whitespace-nowrap"
             data-testid="ad-gate-cta">
            Get the app <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Mobile full-screen intercept */}
      {showSheet && (
        <div className="md:hidden fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-md flex items-end"
             data-testid="ad-gate-sheet"
             onClick={dismissSheet}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full bg-white rounded-t-3xl p-6 pt-8 relative shadow-2xl">
            <button onClick={dismissSheet}
                    className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 p-1"
                    aria-label="Dismiss"
                    data-testid="ad-gate-sheet-close">
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 text-white flex items-center justify-center mb-4">
                <Smartphone className="w-7 h-7" />
              </div>
              <h2 className="font-display text-2xl text-slate-900">
                {attr.store
                  ? `Open the ${attr.store} offer in the Cyannect App`
                  : "Your offer lives in the Cyannect App"}
              </h2>
              <p className="text-sm text-slate-600 mt-2 max-w-xs">
                {attr.store
                  ? `Download Cyannect and jump straight to the ${attr.store} storefront to activate the offer.`
                  : "Coupons, cashback, and gift-card redemption are all inside the app. Download it to see this offer."}
              </p>
              <div className="mt-6 flex flex-col gap-2 w-full max-w-xs">
                <a href="#" title="Coming soon" aria-disabled="true"
                   className="w-full px-4 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-2"
                   data-testid="ad-gate-app-ios">
                  <Apple className="w-4 h-4" /> Download on the App Store
                </a>
                <a href="#" title="Coming soon" aria-disabled="true"
                   className="w-full px-4 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-2"
                   data-testid="ad-gate-app-android">
                  <Play className="w-4 h-4" /> Get it on Google Play
                </a>
              </div>
              <div className="mt-5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Or scan with another device
              </div>
              {qr && <img src={qr} alt="Scan to open Cyannect" className="w-40 h-40 mt-3" data-testid="ad-gate-qr" />}
              <button onClick={dismissSheet}
                      className="mt-6 text-xs text-slate-500 underline"
                      data-testid="ad-gate-continue">
                Continue reading on the web
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
